import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { requireCapability } from "@/lib/authz/authz.server";

/* ============================================================
 * F30.2 — Page Builder backend persistence.
 * One normalised document model: { blocks, responsive, meta }.
 * Mobile is the base; tablet/desktop are overrides stored INSIDE
 * each block — never as separate whole documents.
 * ============================================================ */

const docSchema = z.object({
  blocks: z.array(z.unknown()).default([]),
  meta: z.record(z.string(), z.unknown()).optional(),
});

const seoSchema = z.record(z.string(), z.unknown());

/* ── Admin bootstrap ────────────────────────────────────────── */

/**
 * Idempotent first-admin provisioning. Grants the `admin` role to the
 * CURRENT authenticated caller only when the project has no admin at all.
 * No user id is hardcoded; once an admin exists this becomes a no-op.
 */
export const bootstrapFirstAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Any administrative role counts as "the platform already has an owner".
    // Counting only the literal 'admin' role would leave this escape hatch open
    // forever on installs bootstrapped with 'super_admin'.
    const ADMIN_ROLES = ["admin", "super_admin"] as const;
    const { count, error } = await supabaseAdmin
      .from("user_roles")
      .select("id", { count: "exact", head: true })
      .in("role", [...ADMIN_ROLES]);
    if (error) throw new Error(error.message);

    if ((count ?? 0) > 0) {
      const { data: mine } = await supabaseAdmin
        .from("user_roles")
        .select("id")
        .eq("user_id", context.userId)
        .in("role", [...ADMIN_ROLES])
        .limit(1)
        .maybeSingle();
      return { ok: !!mine, reason: mine ? "already_admin" : "admin_exists" } as const;
    }

    // Security: an empty-admin install must not let any signed-in account claim admin.
    // Only the operator-designated email (server secret ADMIN_BOOTSTRAP_EMAIL) may bootstrap.
    const designated = (process.env.ADMIN_BOOTSTRAP_EMAIL ?? "").trim().toLowerCase();
    const callerEmail = String((context.claims as { email?: unknown } | undefined)?.email ?? "")
      .trim()
      .toLowerCase();
    if (!designated || callerEmail !== designated) {
      return { ok: false, reason: "bootstrap_not_authorized" } as const;
    }

    const { error: insErr } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: context.userId, role: "admin" });
    if (insErr) throw new Error(insErr.message);
    return { ok: true, reason: "bootstrapped" } as const;
  });

/** Whether the signed-in caller currently holds the admin role. */
export const getAdminStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    return { isAdmin: !!data, userId: context.userId };
  });

/* ── Admin: pages ───────────────────────────────────────────── */

export const listBuilderPagesFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireCapability(context, "page_builder.read");
    const { data, error } = await context.supabase
      .from("builder_pages")
      .select("id,slug,title,template,locale,status,published_at,updated_at,updated_by")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getBuilderPageFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.read");
    const { data: page, error } = await context.supabase
      .from("builder_pages")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return page;
  });

export const createBuilderPageFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        title: z.string().trim().min(1).max(200),
        slug: z
          .string()
          .trim()
          .min(1)
          .max(200)
          .regex(/^[a-z0-9_\u0600-\u06FF/-]+$/u, "slug"),
        template: z.string().max(40).default("default"),
        locale: z.enum(["fa", "en"]).default("fa"),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const { data: page, error } = await context.supabase
      .from("builder_pages")
      .insert({
        ...(data.id ? { id: data.id } : {}),
        title: data.title,
        slug: data.slug.replace(/^\/+|\/+$/g, ""),
        template: data.template,
        locale: data.locale,
        status: "draft",
        draft_doc: { blocks: [] },
        updated_by: context.userId,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return page;
  });

/** Autosave / explicit save of the draft document. */
export const saveBuilderDraftFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        doc: docSchema,
        title: z.string().trim().min(1).max(200).optional(),
        seo: seoSchema.optional(),
        snapshot: z.boolean().default(false),
        label: z.string().max(120).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const patch: Record<string, unknown> = {
      draft_doc: data.doc,
      updated_by: context.userId,
    };
    /* eslint-disable @typescript-eslint/no-explicit-any */
    if (data.title) patch.title = data.title;
    if (data.seo) patch.seo = data.seo;

    const { data: page, error } = await context.supabase
      .from("builder_pages")
      .update(patch as any)
      .eq("id", data.id)
      .select("id,updated_at,status")
      .single();
    if (error) throw new Error(error.message);

    if (data.snapshot) {
      await context.supabase.from("builder_revisions").insert({
        page_id: data.id,
        label: data.label ?? "ذخیره دستی",
        doc: data.doc as any,
        created_by: context.userId,
      });
    }
    return page;
  });

export const publishBuilderPageFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const { data: current, error: readErr } = await context.supabase
      .from("builder_pages")
      .select("draft_doc")
      .eq("id", data.id)
      .single();
    if (readErr) throw new Error(readErr.message);

    const { data: page, error } = await context.supabase
      .from("builder_pages")
      .update({
        published_doc: current.draft_doc,
        status: "published",
        published_at: new Date().toISOString(),
        updated_by: context.userId,
      })
      .eq("id", data.id)
      .select("id,slug,status,published_at")
      .single();
    if (error) throw new Error(error.message);

    await context.supabase.from("builder_revisions").insert({
      page_id: data.id,
      label: "انتشار",
      doc: current.draft_doc,
      created_by: context.userId,
    });
    return page;
  });

export const unpublishBuilderPageFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const { error } = await context.supabase
      .from("builder_pages")
      .update({
        status: "draft",
        published_doc: null,
        published_at: null,
        updated_by: context.userId,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteBuilderPageFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const { error } = await context.supabase.from("builder_pages").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* ── Admin: revisions ───────────────────────────────────────── */

export const listBuilderRevisionsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ pageId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.read");
    const { data: rows, error } = await context.supabase
      .from("builder_revisions")
      .select("id,label,created_at,created_by")
      .eq("page_id", data.pageId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const restoreBuilderRevisionFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ pageId: z.string().uuid(), revisionId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const { data: rev, error: revErr } = await context.supabase
      .from("builder_revisions")
      .select("doc,label")
      .eq("id", data.revisionId)
      .eq("page_id", data.pageId)
      .single();
    if (revErr) throw new Error(revErr.message);

    /* F30.2C — restore is DRAFT-ONLY and fully audited:
     * 1) snapshot the draft that is about to be replaced (nothing is lost),
     * 2) write the restored doc to `draft_doc` so it survives a hard refresh,
     * 3) `published_doc` / `status` are never touched — the live page only
     *    changes when the admin explicitly publishes again (which itself
     *    writes a fresh revision). */
    const { data: currentPage } = await context.supabase
      .from("builder_pages")
      .select("draft_doc,status")
      .eq("id", data.pageId)
      .single();

    if (currentPage?.draft_doc) {
      await context.supabase.from("builder_revisions").insert({
        page_id: data.pageId,
        label: `پیش‌نویس پیش از بازگردانی (مبدأ: ${rev.label ?? data.revisionId.slice(0, 8)})`,

        doc: currentPage.draft_doc as any,
        created_by: context.userId,
      });
    }

    const { error } = await context.supabase
      .from("builder_pages")

      .update({ draft_doc: rev.doc as any, updated_by: context.userId })
      .eq("id", data.pageId);
    if (error) throw new Error(error.message);

    return {
      ok: true,
      doc: rev.doc,
      restoredBy: context.userId,
      revisionId: data.revisionId,
      publishedUntouched: true,
      status: currentPage?.status ?? "draft",
    };
  });

/* ── Admin: global / reusable sections ──────────────────────── */

export const listBuilderSectionsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireCapability(context, "page_builder.read");
    const { data, error } = await context.supabase
      .from("builder_sections")
      .select("*")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const saveBuilderSectionFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        key: z.string().max(80).optional(),
        name: z.string().trim().min(1).max(160),
        kind: z
          .enum(["section", "header", "footer", "popup", "cta", "trust-badges"])
          .default("section"),
        is_global: z.boolean().default(false),
        is_published: z.boolean().default(false),
        blocks: z.array(z.unknown()).default([]),
        settings: z.record(z.string(), z.unknown()).default({}),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");

    const row = { ...data, updated_by: context.userId } as any;
    const { data: saved, error } = data.id
      ? await context.supabase
          .from("builder_sections")
          .update(row)
          .eq("id", data.id)
          .select("*")
          .single()
      : await context.supabase.from("builder_sections").insert(row).select("*").single();
    if (error) throw new Error(error.message);
    return saved;
  });

export const deleteBuilderSectionFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireCapability(context, "page_builder.manage");
    const { error } = await context.supabase.from("builder_sections").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
